import Phaser from "phaser";
import {
  ArtPipeline,
  affiliationForBinaryTeam,
  facingDirFromAim,
} from "../../../art";
import {
  frontlineCatalog,
  hexToColor,
} from "../../../data/frontline-catalog";
import {
  VfxPlayer,
  attackTravelSec,
  playFloatText,
  playTimedAttackFx,
  resolveAttackFx,
  resolveAttackOrigin,
  type AttackFxTables,
} from "../../../vfx";
import {
  UnitRig,
  createHpBar,
  dropfrontGripPack,
  frontlineGripPack,
  updateHpBar,
  type GripPack,
  type RigTables,
} from "../../../view/unit-rig";
import {
  baseCenter,
  canDeployAt,
  displayScaleForTier,
  isHqCell,
  type FrontlineState,
  type FlUnit,
  type TeamId,
} from "../frontline-rules";
import { FlEvent, type FlSimEvent } from "../frontline-events";
import { FlDepth, flBodyDepth } from "./fl-layers";
import {
  boltTravelMs,
  isSiegeBoltRole,
  siegeShellTravelMs,
  spawnBoltTrail,
} from "./projectile-trail";

const WALK_ENTER = 22;
const VFX_LAYER = { depth: FlDepth.fx, floatDepth: FlDepth.float } as const;
const HQ_PACK = frontlineCatalog.view.hqArtPack;
const HQ_ROLE = frontlineCatalog.view.hqRole;
const RIG = frontlineCatalog.view.rig as RigTables;
const ATTACK_FX = frontlineCatalog.view.attackFx as AttackFxTables;
const HIT_BUDGET = 10;
const SHOT_BUDGET = 8;
const CELL = frontlineCatalog.balance.cellW;

type UnitArt = { pack: string; role: string };

type UnitView = {
  /** Body + rig only (idle/active Y-sort band). */
  root: Phaser.GameObjects.Container;
  /** HP + level badge — always FlDepth.unitChrome. */
  chrome: Phaser.GameObjects.Container;
  body: Phaser.GameObjects.Image;
  rig: UnitRig;
  hpBg: Phaser.GameObjects.Rectangle;
  hpGhost: Phaser.GameObjects.Rectangle;
  hpFill: Phaser.GameObjects.Rectangle;
  tierText: Phaser.GameObjects.Text;
  baseSize: number;
  barW: number;
  unitId: string;
  art: UnitArt;
};

type HqView = {
  root: Phaser.GameObjects.Container;
  chrome: Phaser.GameObjects.Container;
  body: Phaser.GameObjects.Image;
  hpBg: Phaser.GameObjects.Rectangle;
  hpGhost: Phaser.GameObjects.Rectangle;
  hpFill: Phaser.GameObjects.Rectangle;
  owner: TeamId;
};

function artForUnit(unitId: string): UnitArt {
  const map = (
    frontlineCatalog.view.units as Record<string, UnitArt | undefined>
  )[unitId];
  return map ?? { pack: "dropfront", role: unitId };
}

function gripForPack(pack: string): GripPack {
  return pack === "frontline" ? frontlineGripPack : dropfrontGripPack;
}

/**
 * Frontline presentation — territory + HQ + UnitRig + promote/skill FX.
 */
export class FrontlinePresentation {
  private readonly scene: Phaser.Scene;
  private readonly vfx: VfxPlayer;
  private board!: Phaser.GameObjects.Container;
  private cells: Phaser.GameObjects.Rectangle[] = [];
  private hqAlly!: HqView;
  private hqEnemy!: HqView;
  private preview!: Phaser.GameObjects.Rectangle;
  private aoeRing!: Phaser.GameObjects.Ellipse;
  private hintGfx!: Phaser.GameObjects.Graphics;
  private aoeGfx!: Phaser.GameObjects.Graphics;
  private boardWarn!: Phaser.GameObjects.Rectangle;
  private readonly views = new Map<number, UnitView>();
  private readonly sucking = new Set<number>();
  private trailBudget = { left: 12 };
  private aimHighlightIds = new Set<number>();
  private hitBudget = HIT_BUDGET;
  private shotBudget = SHOT_BUDGET;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.vfx = new VfxPlayer(scene);
  }

  create(state: FrontlineState): void {
    this.board = this.scene.add.container(0, 0).setDepth(FlDepth.board);
    const colors = frontlineCatalog.view.colors;
    for (let r = 0; r < state.rows; r++) {
      for (let c = 0; c < state.cols; c++) {
        const x = state.originX + (c + 0.5) * state.cellW;
        const y = state.originY + (r + 0.5) * state.cellH;
        const rect = this.scene.add
          .rectangle(
            x,
            y,
            state.cellW - 2,
            state.cellH - 2,
            hexToColor(colors.neutral),
            0.85,
          )
          .setStrokeStyle(1, hexToColor(colors.grid), 0.5);
        this.board.add(rect);
        this.cells.push(rect);
      }
    }

    this.hqEnemy = this.createHqView(state, 1);
    this.hqAlly = this.createHqView(state, 0);

    this.preview = this.scene.add
      .rectangle(0, 0, state.cellW - 4, state.cellH - 4, 0xffffff, 0.35)
      .setDepth(FlDepth.preview)
      .setVisible(false);
    this.aoeRing = this.scene.add
      .ellipse(0, 0, 96, 96, 0x7dffb0, 0.18)
      .setStrokeStyle(2, 0x7dffb0, 0.85)
      .setDepth(FlDepth.preview)
      .setVisible(false);
    this.hintGfx = this.scene.add.graphics().setDepth(FlDepth.preview);
    this.aoeGfx = this.scene.add.graphics().setDepth(FlDepth.preview);
    const bw = state.cols * state.cellW;
    const bh = state.rows * state.cellH;
    this.boardWarn = this.scene.add
      .rectangle(
        state.originX + bw * 0.5,
        state.originY + bh * 0.5,
        bw,
        bh,
        0xff4d6d,
        0.12,
      )
      .setDepth(FlDepth.preview)
      .setVisible(false);
  }

  /** Reposition board cells / chrome after HUD-driven board metrics change. */
  relayoutBoard(state: FrontlineState): void {
    let i = 0;
    for (let r = 0; r < state.rows; r++) {
      for (let c = 0; c < state.cols; c++) {
        const rect = this.cells[i++];
        if (!rect) continue;
        rect.setPosition(
          state.originX + (c + 0.5) * state.cellW,
          state.originY + (r + 0.5) * state.cellH,
        );
        rect.setSize(state.cellW - 2, state.cellH - 2);
      }
    }
    this.preview.setSize(state.cellW - 4, state.cellH - 4);
    const bw = state.cols * state.cellW;
    const bh = state.rows * state.cellH;
    this.boardWarn.setPosition(
      state.originX + bw * 0.5,
      state.originY + bh * 0.5,
    );
    this.boardWarn.setSize(bw, bh);
    const fp = frontlineCatalog.balance.hqFootprint;
    for (const hq of [this.hqAlly, this.hqEnemy]) {
      const w = fp.cols * state.cellW * 0.95;
      const h = fp.rows * state.cellH * 1.05;
      hq.body.setDisplaySize(w, h);
    }
  }

  destroy(): void {
    for (const v of this.views.values()) {
      v.rig.destroy();
      v.root.destroy(true);
      v.chrome.destroy(true);
    }
    this.views.clear();
    this.sucking.clear();
    this.board?.destroy(true);
    this.hqAlly?.root.destroy(true);
    this.hqAlly?.chrome.destroy(true);
    this.hqEnemy?.root.destroy(true);
    this.hqEnemy?.chrome.destroy(true);
    this.preview?.destroy();
    this.aoeRing?.destroy();
    this.hintGfx?.destroy();
    this.aoeGfx?.destroy();
    this.boardWarn?.destroy();
  }

  setPreview(
    visible: boolean,
    x: number,
    y: number,
    ok: boolean,
  ): void {
    this.preview.setVisible(visible);
    if (!visible) return;
    this.preview.setPosition(x, y);
    this.preview.setFillStyle(ok ? 0x7dffb0 : 0xff8a80, 0.45);
  }

  setAoePreview(
    visible: boolean,
    x: number,
    y: number,
    radius: number,
    ok: boolean,
  ): void {
    this.aoeRing.setVisible(visible);
    if (!visible) return;
    const d = Math.max(16, radius * 2);
    this.aoeRing.setPosition(x, y);
    this.aoeRing.setDisplaySize(d, d);
    const col = ok ? 0x7dffb0 : 0xff8a80;
    this.aoeRing.setFillStyle(col, 0.16);
    this.aoeRing.setStrokeStyle(2, col, 0.9);
  }

  /** Cell Chebyshev footprint preview (replaces circle for AoE cards). */
  setAoeCellFootprint(
    state: FrontlineState,
    originCol: number,
    originRow: number,
    cellRange: number,
    ok: boolean,
  ): void {
    this.aoeRing.setVisible(false);
    this.aoeGfx.clear();
    const fill = ok ? 0x7dffb0 : 0xff8a80;
    this.aoeGfx.fillStyle(fill, 0.2);
    this.aoeGfx.lineStyle(2, fill, 0.85);
    for (let r = originRow - cellRange; r <= originRow + cellRange; r++) {
      for (let c = originCol - cellRange; c <= originCol + cellRange; c++) {
        if (c < 0 || r < 0 || c >= state.cols || r >= state.rows) continue;
        const d = Math.max(
          Math.abs(c - originCol),
          Math.abs(r - originRow),
        );
        if (d > cellRange) continue;
        const cx = state.originX + (c + 0.5) * state.cellW;
        const cy = state.originY + (r + 0.5) * state.cellH;
        const w = state.cellW - 4;
        const h = state.cellH - 4;
        this.aoeGfx.fillRect(cx - w * 0.5, cy - h * 0.5, w, h);
        this.aoeGfx.strokeRect(cx - w * 0.5, cy - h * 0.5, w, h);
      }
    }
  }

  clearAoeCellFootprint(): void {
    this.aoeGfx.clear();
  }

  flashAoeCells(
    cells: { x: number; y: number }[],
    color: number,
  ): void {
    for (const c of cells) {
      const flash = this.scene.add
        .rectangle(
          c.x,
          c.y,
          frontlineCatalog.balance.cellW - 6,
          frontlineCatalog.balance.cellH - 6,
          color,
          0.45,
        )
        .setDepth(FlDepth.fx);
      this.scene.tweens.add({
        targets: flash,
        alpha: 0,
        duration: 380,
        ease: "Quad.easeOut",
        onComplete: () => flash.destroy(),
      });
    }
  }

  /** Tint valid deploy cells for legion/champion/horde/titan. */
  setOwnedDeployHints(state: FrontlineState, team: TeamId, on: boolean): void {
    this.hintGfx.clear();
    if (!on) return;
    this.hintGfx.fillStyle(0x7dffb0, 0.22);
    this.hintGfx.lineStyle(1, 0x7dffb0, 0.7);
    for (let r = 0; r < state.rows; r++) {
      for (let c = 0; c < state.cols; c++) {
        if (!canDeployAt(state, team, c, r)) continue;
        const cx = state.originX + (c + 0.5) * state.cellW;
        const cy = state.originY + (r + 0.5) * state.cellH;
        const w = state.cellW - 4;
        const h = state.cellH - 4;
        this.hintGfx.fillRect(cx - w * 0.5, cy - h * 0.5, w, h);
        this.hintGfx.strokeRect(cx - w * 0.5, cy - h * 0.5, w, h);
      }
    }
  }

  setBoardWarn(visible: boolean): void {
    this.boardWarn.setVisible(visible);
  }

  clearCastPreview(): void {
    this.setPreview(false, 0, 0, false);
    this.setAoePreview(false, 0, 0, 0, false);
    this.clearAoeCellFootprint();
    this.hintGfx.clear();
    this.boardWarn.setVisible(false);
    this.aimHighlightIds.clear();
  }

  sync(state: FrontlineState, events: FlSimEvent[]): void {
    this.hitBudget = HIT_BUDGET;
    this.shotBudget = SHOT_BUDGET;
    this.paintTerritory(state);
    this.syncHq(state);
    this.handleEvents(state, events);
    this.syncUnits(state);
  }

  private createHqView(state: FrontlineState, owner: TeamId): HqView {
    const aff = affiliationForBinaryTeam(owner);
    const { key } = ArtPipeline.ensureAnimFrame(
      this.scene,
      HQ_PACK,
      HQ_ROLE,
      aff,
      "idle",
      0,
    );
    const fp = frontlineCatalog.balance.hqFootprint;
    const w = fp.cols * state.cellW * 0.95;
    const h = fp.rows * state.cellH * 1.05;
    const c = baseCenter(state, owner);
    const barH = frontlineCatalog.view.layout.hpBarHeight;
    const barW = w * 0.85;
    const colors = frontlineCatalog.view.colors;
    const body = this.scene.add.image(0, 0, key).setDisplaySize(w, h);
    const fillHex = owner === 0 ? colors.hpBarAlly : colors.hpBarEnemy;
    const { bg: hpBg, ghost: hpGhost, fill: hpFill } = createHpBar(this.scene, {
      barW,
      barH,
      y: -h * 0.55,
      bgColor: hexToColor(colors.hpBarBg),
      fillColor: hexToColor(fillHex),
      bgAlpha: 0.8,
    });
    const root = this.scene.add
      .container(c.x, c.y, [body])
      .setDepth(FlDepth.hq);
    const chrome = this.scene.add
      .container(c.x, c.y, [hpBg, hpGhost, hpFill])
      .setDepth(FlDepth.unitChrome);
    hpBg.setVisible(false);
    hpGhost.setVisible(false);
    hpFill.setVisible(false);
    return { root, chrome, body, hpBg, hpGhost, hpFill, owner };
  }

  private syncHq(state: FrontlineState): void {
    const barH = frontlineCatalog.view.layout.hpBarHeight;
    const boardH = state.rows * state.cellH;
    for (const hq of [this.hqAlly, this.hqEnemy]) {
      const c = baseCenter(state, hq.owner);
      hq.root.setPosition(c.x, c.y);
      hq.chrome.setPosition(c.x, c.y);
      const max = state.baseMaxHp[hq.owner]!;
      const hp = state.baseHp[hq.owner]!;
      const damaged = hp < max;
      const ratio = Math.max(0, Math.min(1, hp / Math.max(1, max)));
      const colors = frontlineCatalog.view.colors;
      updateHpBar(
        { bg: hq.hpBg, ghost: hq.hpGhost, fill: hq.hpFill },
        {
          ratio,
          visible: damaged,
          barH,
          fillColor: hexToColor(
            hq.owner === 0 ? colors.hpBarAlly : colors.hpBarEnemy,
          ),
        },
      );
      hq.root.setDepth(
        damaged
          ? flBodyDepth(FlDepth.unitActive, c.y, state.originY, boardH)
          : FlDepth.hq,
      );
      hq.chrome.setDepth(FlDepth.unitChrome);
    }
  }

  private paintTerritory(state: FrontlineState): void {
    const colors = frontlineCatalog.view.colors;
    for (let i = 0; i < this.cells.length; i++) {
      const col = i % state.cols;
      const row = Math.floor(i / state.cols);
      const o = state.owner[i]!;
      let fill =
        o === 0
          ? colors.ownedAlly
          : o === 1
            ? colors.ownedEnemy
            : colors.neutral;
      if (isHqCell(state, col, row)) {
        fill = o === 0 ? colors.hqAlly : colors.hqEnemy;
      }
      const cell = this.cells[i]!;
      cell.setFillStyle(hexToColor(fill), 0.85);
      if (isHqCell(state, col, row)) {
        cell.setStrokeStyle(2, hexToColor(colors.hpBar), 0.7);
      } else {
        cell.setStrokeStyle(1, hexToColor(colors.grid), 0.5);
      }
    }
  }

  private syncUnits(state: FrontlineState): void {
    const live = new Set(state.units.map((u) => u.id));
    for (const id of [...this.views.keys()]) {
      if (live.has(id) || this.sucking.has(id)) continue;
      this.destroyView(id);
    }
    this.trailBudget.left = 12;
    const colors = frontlineCatalog.view.colors;
    const barH = frontlineCatalog.view.layout.hpBarHeight;
    const boardH = state.rows * state.cellH;
    for (const u of state.units) {
      let view = this.views.get(u.id);
      if (view && view.unitId !== u.unitId) {
        this.destroyView(u.id);
        view = undefined;
      }
      if (!view) {
        view = this.createUnitView(u);
        this.views.set(u.id, view);
      }
      const scale = displayScaleForTier(u.tier);
      const size = view.baseSize * scale;
      const barW = size * 0.9;
      view.root.setPosition(u.x, u.y);
      view.chrome.setPosition(u.x, u.y);
      view.root.setAlpha(1);
      view.chrome.setAlpha(1);
      view.body.setDisplaySize(size, size);
      view.barW = barW;
      view.hpBg.setPosition(0, -size * 0.55);
      const damaged = u.hp < u.maxHp;
      const ratio = Math.max(0, Math.min(1, u.hp / Math.max(1, u.maxHp)));
      updateHpBar(
        { bg: view.hpBg, ghost: view.hpGhost, fill: view.hpFill },
        {
          ratio,
          visible: damaged,
          barW,
          barH,
          fillColor: hexToColor(
            u.team === 0 ? colors.hpBarAlly : colors.hpBarEnemy,
          ),
        },
      );
      const bodyBase = damaged ? FlDepth.unitActive : FlDepth.unitIdle;
      view.root.setDepth(
        flBodyDepth(bodyBase, u.y, state.originY, boardH),
      );
      view.chrome.setDepth(FlDepth.unitChrome);
      this.setLevelBadge(view, u, size);
      if (this.aimHighlightIds.has(u.id)) {
        view.body.setTint(0x7dffb0);
      } else if (u.aegisT > 0) {
        view.body.setTint(0xfff3c4);
      } else if (u.surgeT > 0) {
        view.body.setTint(0xffe08a);
      } else if (u.stunT > 0) {
        view.body.setTint(0x9aa4b8);
      } else {
        view.body.clearTint();
      }

      // Intent only — soft-sep residual vx/vy must not flip walk (backline tremble).
      const swinging = u.attackAnimT > 0;
      const intentSpd = Math.hypot(u.intentVx, u.intentVy);
      const moving = !swinging && !u.arrived && intentSpd > WALK_ENTER;
      const animState = swinging ? "attack" : moving ? "walk" : "idle";
      const animTime = swinging
        ? Math.max(0, frontlineCatalog.balance.attackSwingSec - u.attackAnimT)
        : state.time;
      const facingDir = facingDirFromAim(u.aimAngle);
      const aff = affiliationForBinaryTeam(u.team);
      const { key } = ArtPipeline.ensureAnimFrame(
        this.scene,
        view.art.pack,
        view.art.role,
        aff,
        animState,
        animTime,
        facingDir,
      );
      view.rig.setBodyTexture(key);
      view.rig.update(
        {
          aimAngle: u.aimAngle,
          animState,
          animTime,
          intentVx: u.intentVx,
          intentVy: u.intentVy,
          facing: u.facing as 1 | -1,
          dying: false,
          lockBodyFlip: true,
          facingDir,
        },
        this.trailBudget,
        this.scene,
        { x: u.x, y: u.y },
      );
    }
  }

  private setLevelBadge(view: UnitView, u: FlUnit, size: number): void {
    const label =
      u.tier >= 2 ? `Lv${u.level}·T${u.tier}` : `Lv${u.level}`;
    view.tierText.setText(label);
    view.tierText.setVisible(true);
    view.tierText.setColor(
      u.tier >= 3 ? "#ffe08a" : u.tier >= 2 ? "#a0c4ff" : "#c8d0d8",
    );
    view.tierText.setPosition(0, -size * 0.72);
  }

  setAimHighlight(unitId: number | null): void {
    this.aimHighlightIds.clear();
    if (unitId != null) this.aimHighlightIds.add(unitId);
  }

  setAimHighlights(ids: number[]): void {
    this.aimHighlightIds = new Set(ids);
  }

  private createUnitView(u: FlUnit): UnitView {
    const art = artForUnit(u.unitId);
    const aff = affiliationForBinaryTeam(u.team);
    const { key } = ArtPipeline.ensureAnimFrame(
      this.scene,
      art.pack,
      art.role,
      aff,
      "idle",
      0,
      "s",
    );
    const baseSize = 44;
    const colors = frontlineCatalog.view.colors;
    const barH = frontlineCatalog.view.layout.hpBarHeight;
    const barW = baseSize * 0.9;
    const body = this.scene.add.image(0, 0, key).setDisplaySize(baseSize, baseSize);
    const rig = new UnitRig(
      this.scene,
      body,
      art.role,
      RIG,
      WALK_ENTER,
      undefined,
      gripForPack(art.pack),
    );
    const fillHex = u.team === 0 ? colors.hpBarAlly : colors.hpBarEnemy;
    const { bg: hpBg, ghost: hpGhost, fill: hpFill } = createHpBar(this.scene, {
      barW,
      barH,
      y: -baseSize * 0.55,
      bgColor: hexToColor(colors.hpBarBg),
      fillColor: hexToColor(fillHex),
      bgAlpha: 0.75,
    });
    const tierText = this.scene.add
      .text(0, -baseSize * 0.72, "", {
        fontFamily: "monospace",
        fontSize: "11px",
        color: "#a0c4ff",
        stroke: "#000000",
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setVisible(false);
    const root = this.scene.add
      .container(u.x, u.y, [rig.container])
      .setDepth(FlDepth.unitIdle);
    const chrome = this.scene.add
      .container(u.x, u.y, [hpBg, hpGhost, hpFill, tierText])
      .setDepth(FlDepth.unitChrome);
    hpBg.setVisible(false);
    hpGhost.setVisible(false);
    hpFill.setVisible(false);
    return {
      root,
      chrome,
      body,
      rig,
      hpBg,
      hpGhost,
      hpFill,
      tierText,
      baseSize,
      barW,
      unitId: u.unitId,
      art,
    };
  }

  private destroyView(id: number): void {
    const v = this.views.get(id);
    if (!v) return;
    v.rig.destroy();
    v.root.destroy(true);
    v.chrome.destroy(true);
    this.views.delete(id);
  }

  private playAttackImpact(
    state: FrontlineState,
    opts: {
      role: string;
      attackerId?: number;
      x0: number;
      y0: number;
      x1: number;
      y1: number;
      damage: number;
      targetId?: number;
      structure?: boolean;
    },
  ): void {
    if (this.hitBudget-- <= 0) return;
    const fx = resolveAttackFx(ATTACK_FX, opts.role);

    let muzzle: { x: number; y: number } | null = null;
    if (opts.attackerId != null) {
      const av = this.views.get(opts.attackerId);
      if (av) {
        muzzle = av.rig.muzzleWorld({ x: av.root.x, y: av.root.y });
      }
    }
    const { x: ox, y: oy } = resolveAttackOrigin(opts.x0, opts.y0, muzzle);

    const angle = Math.atan2(opts.y1 - oy, opts.x1 - ox);
    const heavy = isSiegeBoltRole(opts.role);
    const attacker = opts.attackerId != null
      ? state.units.find((u) => u.id === opts.attackerId)
      : undefined;
    const boltTint =
      attacker?.team === 1
        ? hexToColor(frontlineCatalog.view.colors.boltEnemy)
        : hexToColor(frontlineCatalog.view.colors.boltAlly);
    const affiliation = affiliationForBinaryTeam(attacker?.team ?? 0);
    const victim =
      opts.targetId != null ? this.views.get(opts.targetId) : undefined;
    const hitTargets = opts.structure
      ? undefined
      : {
          victim: victim?.root,
          victimBody: victim?.body,
          victimRig: victim?.rig?.container ?? victim?.body,
        };

    const distCells =
      Math.hypot(opts.x1 - ox, opts.y1 - oy) / Math.max(1, CELL);
    const catalogSec = attackTravelSec(
      fx,
      distCells,
      frontlineCatalog.balance.attackSwingSec,
    );
    const travelOverrideSec =
      fx.delivery === "bolt"
        ? heavy
          ? siegeShellTravelMs(ox, oy, opts.x1, opts.y1) / 1000
          : Math.max(
              catalogSec,
              boltTravelMs(ox, oy, opts.x1, opts.y1) / 1000,
            )
        : undefined;

    if (this.shotBudget-- <= 0) {
      // Budget exhausted: still schedule hit for bolts so damage read lands.
      if (fx.delivery === "bolt") {
        const ms = Math.round((travelOverrideSec ?? catalogSec) * 1000);
        this.scene.time.delayedCall(ms, () => {
          this.vfx.play(fx.hitPreset, {
            x: opts.x1,
            y: opts.y1,
            angle,
            targets: hitTargets,
            vars: { damage: opts.damage },
            ...VFX_LAYER,
          });
        });
      } else {
        this.vfx.play(fx.hitPreset, {
          x: opts.x1,
          y: opts.y1,
          angle,
          targets: hitTargets,
          vars: { damage: opts.damage },
          ...VFX_LAYER,
        });
      }
      return;
    }

    playTimedAttackFx({
      scene: this.scene,
      vfx: this.vfx,
      fx,
      affiliation,
      packId: fx.spriteFxPackId ?? "frontline",
      originX: ox,
      originY: oy,
      impactX: opts.x1,
      impactY: opts.y1,
      angle,
      swingSec: frontlineCatalog.balance.attackSwingSec,
      cellPx: CELL,
      travelOverrideSec,
      depth: FlDepth.fx,
      floatDepth: FlDepth.float,
      hitTargets,
      damage: opts.damage,
      targetKind: opts.structure ? "structure" : "enemy",
      onBoltFlight:
        fx.delivery === "bolt"
          ? ({ travelMs, playHit }) => {
              spawnBoltTrail(
                this.scene,
                ox,
                oy,
                opts.x1,
                opts.y1,
                travelMs,
                boltTint,
                heavy,
                playHit,
              );
            }
          : undefined,
    });
  }

  private handleEvents(state: FrontlineState, events: FlSimEvent[]): void {
    for (const ev of events) {
      if (ev.kind === FlEvent.Hit) {
        this.playAttackImpact(state, {
          role: ev.role,
          attackerId: ev.attackerId,
          x0: ev.x0,
          y0: ev.y0,
          x1: ev.x1,
          y1: ev.y1,
          damage: ev.damage,
          targetId: ev.targetId,
        });
      } else if (ev.kind === FlEvent.BaseHit) {
        this.playAttackImpact(state, {
          role: ev.role,
          attackerId: ev.attackerId,
          x0: ev.x0,
          y0: ev.y0,
          x1: ev.x1,
          y1: ev.y1,
          damage: ev.damage,
          structure: true,
        });
      } else if (ev.kind === FlEvent.Promote) {
        this.vfx.play("df.volume.pulse", {
          x: ev.x,
          y: ev.y,
          angle: 0,
          ...VFX_LAYER,
        });
        this.vfx.play("df.land", {
          x: ev.x,
          y: ev.y,
          angle: -Math.PI / 2,
          ...VFX_LAYER,
        });
        this.scene.time.delayedCall(0, () => {
          const v = this.views.get(ev.unitId);
          if (!v) return;
          v.body.setTint(0xffe08a);
          this.scene.tweens.add({
            targets: v.root,
            scaleX: 1.35,
            scaleY: 1.35,
            duration: 140,
            yoyo: true,
            ease: "Quad.easeOut",
          });
          this.scene.time.delayedCall(280, () => {
            if (this.views.has(ev.unitId)) v.body.clearTint();
          });
        });
      } else if (ev.kind === FlEvent.Skill) {
        const card = (
          frontlineCatalog.skills.cards as Record<
            string,
            { vfx?: string } | undefined
          >
        )[ev.skillId];
        const preset = card?.vfx ?? "fl.skill.surge";
        this.vfx.play(preset, { x: ev.x, y: ev.y, angle: 0, ...VFX_LAYER });
      } else if (ev.kind === FlEvent.SkillWave) {
        const foe = ev.skillId === "smite" || ev.skillId === "stun";
        const color = foe ? 0xff8a80 : 0x7dffb0;
        this.flashAoeCells(ev.cells, color);
        const card = (
          frontlineCatalog.skills.cards as Record<
            string,
            { vfx?: string } | undefined
          >
        )[ev.skillId];
        const preset = card?.vfx ?? "fl.skill.surge";
        const focus = ev.hits[0] ?? ev.cells[0];
        if (focus) {
          this.vfx.play(preset, {
            x: focus.x,
            y: focus.y,
            angle: 0,
            ...VFX_LAYER,
          });
        }
        for (const hit of ev.hits) {
          if ((hit.heal ?? 0) > 0) {
            playFloatText(
              this.scene,
              hit.x,
              hit.y,
              {
                template: "+{heal}",
                color: "#5dff9a",
                rise: 36,
                duration: 520,
                offsetY: -14,
                fontSize: 14,
              },
              { heal: hit.heal ?? 0 },
              FlDepth.float,
            );
          }
          if ((hit.damage ?? 0) > 0) {
            playFloatText(
              this.scene,
              hit.x,
              hit.y,
              {
                template: "-{damage}",
                color: "#ff8a80",
                rise: 40,
                duration: 560,
                offsetY: -16,
                fontSize: 15,
              },
              { damage: hit.damage ?? 0 },
              FlDepth.float,
            );
          }
        }
      } else if (ev.kind === FlEvent.Deploy) {
        this.vfx.play("df.land", {
          x: ev.x,
          y: ev.y,
          angle: -Math.PI / 2,
          ...VFX_LAYER,
        });
      } else if (ev.kind === FlEvent.Ability) {
        // Visual volume only — numeric float comes from Hit/BaseHit via dealDamage.
        const caster = state.units.find((u) => u.id === ev.casterId);
        const angle = caster?.aimAngle ?? 0;
        const preset =
          ev.abilityId === "shockwave" || ev.abilityId === "bless"
            ? "df.volume.pulse"
            : ev.abilityId === "bombard" || ev.abilityId === "chain"
              ? "df.origin.muzzle"
              : ev.abilityId === "blinkstrike" || ev.abilityId === "spines"
                ? "df.volume.claw"
                : ev.abilityId === "ram"
                  ? "df.volume.slash"
                  : "df.volume.slash";
        this.vfx.play(preset, {
          x: ev.x,
          y: ev.y,
          angle,
          ...VFX_LAYER,
        });
        if (ev.abilityId === "shockwave" || ev.abilityId === "bless") {
          this.vfx.play("df.origin.pulse", {
            x: caster?.x ?? ev.x,
            y: caster?.y ?? ev.y,
            angle: 0,
            ...VFX_LAYER,
          });
        } else if (ev.abilityId === "bombard" && caster) {
          this.vfx.play("df.flight.bolt", {
            x: caster.x,
            y: caster.y,
            angle,
            ...VFX_LAYER,
          });
        } else if (ev.abilityId === "chain" && ev.targetIds) {
          for (const tid of ev.targetIds) {
            const t = state.units.find((u) => u.id === tid);
            if (!t) continue;
            this.vfx.play("df.origin.muzzle", {
              x: t.x,
              y: t.y,
              angle,
              ...VFX_LAYER,
            });
          }
        }
      }
    }
  }
}
